import { createFileRoute, invariant, redirect } from '@tanstack/react-router';
import jwtDecode from 'jwt-decode';
import { queryEntepriseOrgSettings } from 'src/api/queries/enterpriseAdmin';
import type { RmClaims } from 'src/api/queries/user';
import { OffboardingPage } from 'src/apps/enterprise/app/pages/Offboarding';
import { enterpriseHelpers } from 'src/utils/enterpriseHelpers';
import { AccessDeniedError } from 'src/utils/errors';
import { userHelpers } from 'src/utils/userHelpers';

export const Route = createFileRoute(
  '/_auth/enterprise/_with-org/offboarding/'
)({
  async beforeLoad({ context }) {
    const auth = await context.auth;

    invariant(auth.user, 'User must be logged in to access this route.');

    const accessToken = await auth.getAccessTokenSilently();
    const claims: RmClaims | undefined = await jwtDecode(accessToken);

    invariant(claims, 'Access token claims are required.');

    if (!userHelpers.hasPermissionReadOrganizationSettings(claims)) {
      throw new AccessDeniedError(
        'You do not have permission to view this page.'
      );
    }

    const orgSettings = await context.queryClient.ensureQueryData(
      queryEntepriseOrgSettings()
    );

    if (!orgSettings) {
      throw new Error('Unable to load offboarding status.');
    }

    if (!enterpriseHelpers.hasStartedOffboarding(orgSettings)) {
      throw redirect({
        from: '/enterprise/offboarding',
        to: '/enterprise/billing',
        replace: true,
      });
    }

    if (!userHelpers.hasPermissionCreateOffboarding(claims)) {
      throw new AccessDeniedError(
        'User does not have permission to offboard this organization.'
      );
    }
  },
  component: OffboardingPage,
});
